import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, Wind, Droplets, Thermometer, Waves, ArrowUp, ArrowDown, Clock } from 'lucide-react';
import { logger } from '../lib/logger';

const HomeWeatherWidget = () => {
  const [weatherData, setWeatherData] = useState({});
  const [tidesData, setTidesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const locations = [
    { name: 'La Rochelle', lat: 46.1591, lon: -1.1520 },
    { name: 'Île de Ré', lat: 46.2044, lon: -1.3847 },
    { name: "Île d'Oléron", lat: 45.9167, lon: -1.3167 }
  ];

  // Weather code to description and icon mapping
  const getWeatherInfo = (code) => {
    const weatherCodes = {
      0: { description: 'Ciel dégagé', icon: <Sun className="text-yellow-400" size={28} /> },
      1: { description: 'Peu nuageux', icon: <Sun className="text-yellow-400" size={28} /> },
      2: { description: 'Partiellement nuageux', icon: <Cloud className="text-gray-300" size={28} /> },
      3: { description: 'Couvert', icon: <Cloud className="text-gray-400" size={28} /> },
      45: { description: 'Brouillard', icon: <Cloud className="text-gray-400" size={28} /> },
      48: { description: 'Brouillard givrant', icon: <Cloud className="text-gray-400" size={28} /> },
      51: { description: 'Bruine légère', icon: <CloudRain className="text-blue-300" size={28} /> },
      53: { description: 'Bruine', icon: <CloudRain className="text-blue-400" size={28} /> },
      55: { description: 'Bruine dense', icon: <CloudRain className="text-blue-500" size={28} /> },
      61: { description: 'Pluie légère', icon: <CloudRain className="text-blue-400" size={28} /> },
      63: { description: 'Pluie modérée', icon: <CloudRain className="text-blue-500" size={28} /> },
      65: { description: 'Pluie forte', icon: <CloudRain className="text-blue-600" size={28} /> },
      80: { description: 'Averses', icon: <CloudRain className="text-blue-400" size={28} /> },
      81: { description: 'Averses modérées', icon: <CloudRain className="text-blue-500" size={28} /> },
      82: { description: 'Averses violentes', icon: <CloudRain className="text-blue-600" size={28} /> },
      95: { description: 'Orage', icon: <CloudRain className="text-purple-500" size={28} /> },
    };
    return weatherCodes[code] || { description: 'Variable', icon: <Cloud className="text-gray-400" size={28} /> };
  };

  // Wind direction to text
  const getWindDirection = (degrees) => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  // Calculate approximate tides for La Rochelle area
  // Based on lunar cycle - simplified model
  const calculateTides = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // La Rochelle average tide times (approximate)
    // High tide occurs roughly every 12h25m
    // Reference: Known high tide time (approximate)
    const lunarDay = 24 * 60 + 50; // 24h50m in minutes
    const halfLunarDay = lunarDay / 2;
    
    // Calculate phase based on a known reference point
    // January 1, 2026 00:00 - approximate high tide reference
    const reference = new Date(2026, 0, 1, 5, 30); // Reference high tide
    const diffMinutes = (now - reference) / (1000 * 60);
    const cyclePosition = diffMinutes % lunarDay;
    
    // Generate tide times for today and tomorrow
    const tides = [];
    const baseTime = new Date(today);
    
    // Find the first high tide of the day
    let firstHighMinutes = (lunarDay - (diffMinutes % lunarDay)) % halfLunarDay;
    if (firstHighMinutes > halfLunarDay) firstHighMinutes -= halfLunarDay;
    
    // Calculate tide coefficient (simplified - varies with lunar phase)
    const moonPhase = (diffMinutes / (29.5 * 24 * 60)) % 1;
    const coefficient = Math.round(70 + 50 * Math.abs(Math.cos(moonPhase * 2 * Math.PI)));
    
    // Generate 4 tides (2 high, 2 low)
    for (let i = 0; i < 4; i++) {
      const tideTime = new Date(baseTime.getTime() + (firstHighMinutes + i * halfLunarDay / 2) * 60 * 1000);
      const isHigh = i % 2 === 0;
      
      // Approximate heights for La Rochelle (in meters)
      const height = isHigh 
        ? (5.0 + (coefficient - 70) * 0.03).toFixed(1)
        : (1.5 - (coefficient - 70) * 0.015).toFixed(1);
      
      if (tideTime > now || i < 2) { // Show upcoming tides
        tides.push({
          time: tideTime,
          type: isHigh ? 'Pleine mer' : 'Basse mer',
          height: height,
          isHigh: isHigh
        });
      }
    }
    
    return {
      tides: tides.slice(0, 4),
      coefficient: coefficient,
      port: 'La Rochelle - La Pallice'
    };
  };

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        const results = {};

        for (const location of locations) {
          const response = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,precipitation&hourly=precipitation_probability&timezone=Europe%2FParis&forecast_hours=12`
          );
          
          if (!response.ok) throw new Error('Erreur météo');
          
          const data = await response.json();
          
          // Get max precipitation probability for next 6 hours
          const precipProb = data.hourly?.precipitation_probability?.slice(0, 6) || [];
          const maxPrecipProb = Math.max(...precipProb, 0);
          
          results[location.name] = {
            current: data.current,
            precipitationProbability: maxPrecipProb
          };
        }

        setWeatherData(results);
        setTidesData(calculateTides());
        setError(null);
      } catch (err) {
        logger.error('Weather fetch error:', err);
        setError('Météo temporairement indisponible');
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <section className="py-12 bg-[#1a1c1b]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="bg-gradient-to-br from-[#1e3a5f]/60 to-[#0d1b2a]/80 rounded-2xl p-8 border border-[#3f4816]/30">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#d9fb06]"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-[#1a1c1b]" id="meteo">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="bg-gradient-to-br from-[#1e3a5f]/60 to-[#0d1b2a]/80 rounded-2xl p-6 md:p-8 border border-[#3f4816]/30">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#d9fb06] flex items-center gap-3">
                <Waves size={28} />
                Météo Marine
              </h2>
              <p className="text-[#888680] text-sm mt-1">La Rochelle et ses îles</p>
            </div>
            {tidesData.coefficient && (
              <div className="mt-3 md:mt-0 bg-[#d9fb06]/10 rounded-lg px-4 py-2 border border-[#d9fb06]/30">
                <span className="text-[#888680] text-sm">Coefficient </span>
                <span className="text-[#d9fb06] font-bold text-xl">{tidesData.coefficient}</span>
              </div>
            )}
          </div>

          {/* Weather Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {locations.map((location) => {
              const data = weatherData[location.name];
              if (!data) return null;

              const weatherInfo = getWeatherInfo(data.current.weather_code);
              const windDir = getWindDirection(data.current.wind_direction_10m);

              return (
                <div 
                  key={location.name}
                  className="bg-[#1a1c1b]/70 rounded-xl p-4 border border-[#3f4816]/30"
                >
                  {/* Location & Icon */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-white">{location.name}</h3>
                    {weatherInfo.icon}
                  </div>

                  {/* Temperature */}
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-3xl font-bold text-[#d9fb06]">
                      {Math.round(data.current.temperature_2m)}°C
                    </span>
                    <span className="text-[#888680] text-xs">{weatherInfo.description}</span>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {/* Wind */}
                    <div className="flex items-center gap-1.5 text-[#dfddd6] bg-[#302f2c]/50 rounded px-2 py-1.5">
                      <Wind size={14} className="text-[#d9fb06]" />
                      <span>{Math.round(data.current.wind_speed_10m)} km/h {windDir}</span>
                    </div>
                    
                    {/* Gusts */}
                    <div className="flex items-center gap-1.5 text-[#dfddd6] bg-[#302f2c]/50 rounded px-2 py-1.5">
                      <Wind size={14} className="text-orange-400" />
                      <span>Raf. {Math.round(data.current.wind_gusts_10m)}</span>
                    </div>
                    
                    {/* Precipitation */}
                    <div className="flex items-center gap-1.5 text-[#dfddd6] bg-[#302f2c]/50 rounded px-2 py-1.5">
                      <CloudRain size={14} className="text-blue-400" />
                      <span>Pluie {data.precipitationProbability}%</span>
                    </div>
                    
                    {/* Humidity */}
                    <div className="flex items-center gap-1.5 text-[#dfddd6] bg-[#302f2c]/50 rounded px-2 py-1.5">
                      <Droplets size={14} className="text-cyan-400" />
                      <span>Hum. {data.current.relative_humidity_2m}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tides Section */}
          <div className="bg-[#1a1c1b]/70 rounded-xl p-4 border border-[#3f4816]/30">
            <div className="flex items-center gap-2 mb-4">
              <Waves size={20} className="text-[#d9fb06]" />
              <h3 className="font-bold text-white">Marées - {tidesData.port}</h3>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {tidesData.tides?.map((tide, idx) => (
                <div 
                  key={idx}
                  className={`rounded-lg p-3 text-center ${
                    tide.isHigh 
                      ? 'bg-blue-500/10 border border-blue-500/30' 
                      : 'bg-cyan-500/10 border border-cyan-500/30'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1 mb-1">
                    {tide.isHigh ? (
                      <ArrowUp size={16} className="text-blue-400" />
                    ) : (
                      <ArrowDown size={16} className="text-cyan-400" />
                    )}
                    <span className={`text-xs font-medium ${tide.isHigh ? 'text-blue-400' : 'text-cyan-400'}`}>
                      {tide.type}
                    </span>
                  </div>
                  <div className="text-white font-bold text-lg">
                    {tide.time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="text-[#888680] text-xs">
                    {tide.height}m
                  </div>
                </div>
              ))}
            </div>
            
            <p className="text-[#888680] text-xs mt-3 text-center">
              Horaires indicatifs - Consultez les sources officielles pour la navigation
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-4 text-xs text-[#888680]">
            <span>Données Open-Meteo</span>
            <a 
              href="https://maree.info/82" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#d9fb06] hover:underline"
            >
              Marées détaillées →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeWeatherWidget;
